import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../../services/api';
import { RingLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { ChevronDownIcon, ChevronUpIcon, UserCircleIcon, MapPinIcon, CalendarDaysIcon, CurrencyDollarIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const AdminOrderManagement = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedOrders, setExpandedOrders] = useState(new Set());
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [statusDescription, setStatusDescription] = useState('');
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [deletingOrder, setDeletingOrder] = useState(false);

    const orderStatuses = [
        'ORDER_RECEIVED',
        'ORDER_ACCEPTED',
        'SHIPPED',
        'DELIVERED',
        'CANCELLED',
        'REFUNDED'
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'ORDER_RECEIVED':
                return 'text-blue-700 bg-blue-50 border-blue-200';
            case 'ORDER_ACCEPTED':
                return 'text-indigo-700 bg-indigo-50 border-indigo-200';
            case 'SHIPPED':
                return 'text-purple-700 bg-purple-50 border-purple-200';
            case 'DELIVERED':
                return 'text-green-700 bg-green-50 border-green-200';
            case 'CANCELLED':
                return 'text-red-700 bg-red-50 border-red-200';
            case 'REFUNDED':
                return 'text-orange-700 bg-orange-50 border-orange-200';
            default:
                return 'text-gray-700 bg-gray-50 border-gray-200';
        }
    };

    const getStatusDotColor = (status) => {
        switch (status) {
            case 'ORDER_RECEIVED':
                return 'bg-blue-500';
            case 'ORDER_ACCEPTED':
                return 'bg-indigo-500';
            case 'SHIPPED':
                return 'bg-purple-500';
            case 'DELIVERED':
                return 'bg-green-500';
            case 'CANCELLED':
                return 'bg-red-500';
            case 'REFUNDED':
                return 'bg-orange-500';
            default:
                return 'bg-gray-500';
        }
    };

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const ordersData = await fetchWithAuth('/admin/orders');
            setOrders(ordersData);
        } catch (err) {
            setError(t('adminOrderManagement.errorFetching'));
            toast.error(t('adminOrderManagement.errorFetchingToast'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    const updateOrderStatus = async (orderId, newStatus, description = '') => {
        try {
            setUpdatingStatus(prev => ({ ...prev, [orderId]: true }));

            const updatedOrder = await fetchWithAuth(`/admin/orders/${orderId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status: newStatus,
                    description: description
                }),
            });

            setOrders(prev => prev.map(order =>
                order.id === orderId ? updatedOrder : order
            ));
            toast.success(t('adminOrderManagement.statusUpdatedToast'));
        } catch (err) {
            toast.error(t('adminOrderManagement.errorUpdatingStatus'));
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const handleStatusChange = (orderId, newStatus, currentStatus) => {
        setPendingStatusChange({
            orderId,
            newStatus,
            currentStatus
        });
        setStatusDescription('');
        setShowConfirmModal(true);
    };

    const confirmStatusChange = async () => {
        if (pendingStatusChange) {
            await updateOrderStatus(
                pendingStatusChange.orderId,
                pendingStatusChange.newStatus,
                statusDescription
            );
        }
        setShowConfirmModal(false);
        setPendingStatusChange(null);
        setStatusDescription('');
    };

    const cancelStatusChange = () => {
        setShowConfirmModal(false);
        setPendingStatusChange(null);
        setStatusDescription('');
    };

    const toggleOrderExpansion = (orderId) => {
        setExpandedOrders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(orderId)) {
                newSet.delete(orderId);
            } else {
                newSet.add(orderId);
            }
            return newSet;
        });
    };

    const confirmOrderDelete = async () => {
        if (!orderToDelete) return;
        try {
            setDeletingOrder(true);
            await fetchWithAuth(`/admin/orders/${orderToDelete.id}`, { method: 'DELETE' });
            setOrders(prev => prev.filter(order => order.id !== orderToDelete.id));
            setExpandedOrders(prev => {
                const next = new Set(prev);
                next.delete(orderToDelete.id);
                return next;
            });
            toast.success(t('adminOrderManagement.orderDeleted', 'Order deleted and QR code returned to available inventory.'));
            setOrderToDelete(null);
        } catch (err) {
            toast.error(err.message || t('adminOrderManagement.errorDeletingOrder', 'Failed to delete order.'));
        } finally {
            setDeletingOrder(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <RingLoader color="#4A90E2" size={80} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <p className="text-red-500 text-xl">{error}</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 bg-gray-50 min-h-screen" data-testid="admin-order-management-page">
            <div className="mb-8">
                <h1 className="text-4xl font-bold text-center text-gray-800 mb-2" data-testid="admin-order-management-title">
                    {t('adminOrderManagement.title')}
                </h1>
                <p className="text-center text-gray-600">
                    {orders.length > 0 ? `${orders.length} orders found` : ''}
                </p>
            </div>

            {orders.length === 0 ? (
                <div className="text-center text-gray-600 py-12" data-testid="admin-orders-no-orders">
                    <div className="text-6xl mb-4">📦</div>
                    <p className="text-xl">{t('adminOrderManagement.noOrdersFound')}</p>
                </div>
            ) : (
                <div className="space-y-6" data-testid="admin-orders-list">
                    {orders.map(order => (
                        <div key={order.id} className="bg-white shadow-lg rounded-lg overflow-hidden" data-testid={`admin-order-${order.id}`}>
                            {/* Order Header */}
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-4 mb-2">
                                            <h2 className="text-xl font-semibold text-indigo-600" data-testid={`admin-order-id-${order.id}`}>
                                                {t('adminOrderManagement.orderIdLabel')} #{order.id}
                                            </h2>
                                            <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(order.status)}`} data-testid={`admin-order-status-${order.id}`}>
                                                {t(`orderStatus.${order.status}`, order.status)}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                                            <div className="flex items-center gap-2">
                                                <UserCircleIcon className="h-4 w-4" />
                                                <span className="font-medium">{t('adminOrderManagement.customerLabel')}:</span>
                                                <span data-testid={`admin-order-customer-${order.id}`}>{order.userName}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <CalendarDaysIcon className="h-4 w-4" />
                                                <span className="font-medium">{t('adminOrderManagement.placedOnLabel')}:</span>
                                                <span data-testid={`admin-order-date-${order.id}`}>{new Date(order.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <CurrencyDollarIcon className="h-4 w-4" />
                                                <span className="font-medium">{t('adminOrderManagement.totalAmountLabel')}:</span>
                                                <span className="font-semibold" data-testid={`admin-order-total-${order.id}`}>{order.totalAmount.toFixed(2)} {t('currency.mkd')}</span>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Email: <span data-testid={`admin-order-email-${order.id}`}>{order.userEmail}</span>
                                            </div>
                                        </div>

                                        {/* QR Code Header Info */}
                                        {order.qrCode && (
                                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM13 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM9 6a1 1 0 000 2v2a1 1 0 001 1h2a1 1 0 100-2V9a1 1 0 00-1-1H9zM6 13a1 1 0 100 2h2a1 1 0 100-2H6zM17 11a1 1 0 100-2h-2a1 1 0 100 2h2zM17 15a1 1 0 01-1 1h-2a1 1 0 110-2h2a1 1 0 011 1zM13 17a1 1 0 100-2v-2a1 1 0 10-2 0v2a1 1 0 001 1h1z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="font-medium text-blue-900">Плочка со QR Code:</span>
                                                    <span className="font-mono font-semibold text-blue-700" data-testid={`admin-order-qr-code-header-${order.id}`}>{order.qrCode}</span>
                                                    {order.productSize && (
                                                        <>
                                                            <span className="text-blue-600">•</span>
                                                            <span className="font-medium text-blue-900">Големина:</span>
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                order.productSize === 'S' ? 'bg-blue-100 text-blue-800' :
                                                                order.productSize === 'M' ? 'bg-green-100 text-green-800' :
                                                                'bg-purple-100 text-purple-800'
                                                            }`}>
                                                                {order.productSize}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {/* Status Update Dropdown */}
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm font-medium text-gray-700">
                                                {t('adminOrderManagement.statusLabel')}:
                                            </label>
                                            <select
                                                value={order.status}
                                                onChange={(e) => handleStatusChange(order.id, e.target.value, order.status)}
                                                disabled={updatingStatus[order.id]}
                                                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                                                data-testid={`admin-order-status-select-${order.id}`}
                                            >
                                                {orderStatuses.map(status => (
                                                    <option key={status} value={status}>
                                                        {t(`orderStatus.${status}`, status)}
                                                    </option>
                                                ))}
                                            </select>
                                            {updatingStatus[order.id] && (
                                                <RingLoader color="#4A90E2" size={20} />
                                            )}
                                        </div>

                                        {/* Expand/Collapse Button */}
                                        <button
                                            onClick={() => toggleOrderExpansion(order.id)}
                                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                            data-testid={`admin-order-toggle-details-${order.id}`}
                                        >
                                            {expandedOrders.has(order.id) ? (
                                                <>
                                                    <ChevronUpIcon className="h-4 w-4" />
                                                    Hide Details
                                                </>
                                            ) : (
                                                <>
                                                    <ChevronDownIcon className="h-4 w-4" />
                                                    {t('adminOrderManagement.viewDetailsButton')}
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => setOrderToDelete(order)}
                                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-700 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                                            data-testid={`admin-order-delete-${order.id}`}
                                            title={t('adminOrderManagement.deleteOrder', 'Delete order')}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                            {t('common.delete', 'Delete')}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Order Details */}
                            {expandedOrders.has(order.id) && (
                                <div className="p-6 bg-gray-50" data-testid={`admin-order-details-${order.id}`}>
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                        {/* QR Code Section */}
                                        {order.qrCode && (
                                            <div className="lg:col-span-3 mb-6" data-testid={`admin-order-qr-code-section-${order.id}`}>
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM13 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM9 6a1 1 0 000 2v2a1 1 0 001 1h2a1 1 0 100-2V9a1 1 0 00-1-1H9zM6 13a1 1 0 100 2h2a1 1 0 100-2H6zM17 11a1 1 0 100-2h-2a1 1 0 100 2h2zM17 15a1 1 0 01-1 1h-2a1 1 0 110-2h2a1 1 0 011 1zM13 17a1 1 0 100-2v-2a1 1 0 10-2 0v2a1 1 0 001 1h1z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-blue-900">Плочка со QR Code:</p>
                                                            <div className="flex items-center gap-3 mt-1">
                                                                <p className="text-xl font-mono font-bold text-blue-700" data-testid={`admin-order-qr-code-${order.id}`}>{order.qrCode}</p>
                                                                {order.productSize && (
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-blue-600">•</span>
                                                                        <span className="text-sm font-medium text-blue-900">Големина:</span>
                                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                            order.productSize === 'S' ? 'bg-blue-100 text-blue-800' :
                                                                            order.productSize === 'M' ? 'bg-green-100 text-green-800' :
                                                                            'bg-purple-100 text-purple-800'
                                                                        }`} data-testid={`admin-order-product-size-${order.id}`}>
                                                                            {order.productSize}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Order Items */}
                                        <div data-testid={`admin-order-items-section-${order.id}`}>
                                            <h3 className="text-lg font-semibold text-gray-700 mb-3">
                                                {t('adminOrderManagement.itemsLabel')}
                                            </h3>
                                            <div className="space-y-3">
                                                {order.orderItems.map(item => (
                                                    <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-lg border" data-testid={`admin-order-item-${item.id}`}>
                                                        <div className="flex items-center gap-3">
                                                            <img
                                                                src={item.productImageUrl || '/placeholder-image.jpg'}
                                                                alt={item.productName}
                                                                className="w-12 h-12 object-cover rounded-md"
                                                                onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-image.jpg'; }}
                                                                data-testid={`admin-order-item-image-${item.id}`}
                                                            />
                                                            <div>
                                                                <p className="font-medium text-gray-800" data-testid={`admin-order-item-name-${item.id}`}>{item.productName}</p>
                                                                <p className="text-sm text-gray-500" data-testid={`admin-order-item-quantity-${item.id}`}>
                                                                    {t('adminOrderManagement.quantityLabel')}: {item.quantity}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="font-semibold text-gray-700" data-testid={`admin-order-item-total-${item.id}`}>
                                                                {item.lineTotal.toFixed(2)} {t('currency.mkd')}
                                                            </p>
                                                            <p className="text-xs text-gray-500" data-testid={`admin-order-item-price-${item.id}`}>
                                                                {item.priceAtPurchase.toFixed(2)} {t('currency.mkd')} each
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Status History */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-700 mb-3">
                                                {t('adminOrderManagement.statusHistoryLabel')}
                                            </h3>
                                            <div className="space-y-3">
                                                {order.statusHistory && order.statusHistory.length > 0 ? (
                                                    order.statusHistory.map((history, index) => (
                                                        <div key={history.id} className="relative">
                                                            <div className="flex items-start gap-4">
                                                                {/* Status Icon */}
                                                                <div className="flex flex-col items-center">
                                                                    <div className={`w-3 h-3 rounded-full ${getStatusDotColor(history.status)} border-2 border-white shadow-md`}></div>
                                                                    {index < order.statusHistory.length - 1 && (
                                                                        <div className="w-0.5 h-8 bg-gray-200 mt-2"></div>
                                                                    )}
                                                                </div>

                                                                {/* Status Content */}
                                                                <div className="flex-1 min-w-0 pb-4">
                                                                    <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                                                                        <div className="flex items-center justify-between mb-1">
                                                                            <h4 className="font-medium text-gray-900">
                                                                                {t(`orderStatus.${history.status}`, history.status)}
                                                                            </h4>
                                                                            <span className="text-xs text-gray-500">
                                                                                {new Date(history.changedAt).toLocaleDateString()} {new Date(history.changedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                                            </span>
                                                                        </div>

                                                                        {history.description && (
                                                                            <p className="text-sm text-gray-600 mb-2">
                                                                                "{history.description}"
                                                                            </p>
                                                                        )}

                                                                        <p className="text-xs text-gray-500 flex items-center gap-1">
                                                                            <span>👤</span>
                                                                            {t('adminOrderManagement.changedByLabel')}: {history.changedBy}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-500">
                                                        <div className="text-2xl mb-2">📋</div>
                                                        {t('adminOrderManagement.noStatusHistory')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Order Summary & Shipping */}
                                        <div className="space-y-4">
                                            {/* Shipping Address */}
                                            {order.shippingAddress && (
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-700 mb-2">
                                                        {t('adminOrderManagement.shippingAddressLabel')}
                                                    </h3>
                                                    <div className="p-3 bg-white rounded-lg border">
                                                        <div className="flex items-start gap-2">
                                                            <MapPinIcon className="h-5 w-5 text-gray-500 mt-0.5" />
                                                            <p className="text-gray-600 whitespace-pre-line">
                                                                {order.shippingAddress}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Order Summary */}
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-700 mb-2">Order Summary</h3>
                                                <div className="p-3 bg-white rounded-lg border space-y-2">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">Subtotal:</span>
                                                        <span>{order.subtotal.toFixed(2)} {t('currency.mkd')}</span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">Shipping:</span>
                                                        <span>{order.shippingCost.toFixed(2)} {t('currency.mkd')}</span>
                                                    </div>
                                                    <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                                                        <span>Total:</span>
                                                        <span>{order.totalAmount.toFixed(2)} {t('currency.mkd')}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Timestamps */}
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-700 mb-2">Timestamps</h3>
                                                <div className="p-3 bg-white rounded-lg border space-y-1 text-sm">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Created:</span>
                                                        <span>{new Date(order.createdAt).toLocaleString()}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Last Updated:</span>
                                                        <span>{new Date(order.updatedAt).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Status Change Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="admin-order-status-modal">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-testid="admin-order-status-modal-title">
                                {t('adminOrderManagement.confirmStatusChange', 'Confirm Status Change')}
                            </h3>

                            {pendingStatusChange && (
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-sm text-gray-600">
                                            <span className="font-medium">From:</span>
                                            <span className={`ml-2 px-2 py-1 rounded text-xs ${getStatusColor(pendingStatusChange.currentStatus)}`} data-testid="admin-order-status-modal-from">
                                                {t(`orderStatus.${pendingStatusChange.currentStatus}`, pendingStatusChange.currentStatus)}
                                            </span>
                                        </div>
                                        <div className="text-gray-400">→</div>
                                        <div className="text-sm text-gray-600">
                                            <span className="font-medium">To:</span>
                                            <span className={`ml-2 px-2 py-1 rounded text-xs ${getStatusColor(pendingStatusChange.newStatus)}`} data-testid="admin-order-status-modal-to">
                                                {t(`orderStatus.${pendingStatusChange.newStatus}`, pendingStatusChange.newStatus)}
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('adminOrderManagement.statusChangeNote', 'Optional Note')}
                                            <span className="text-gray-400 font-normal">({t('adminOrderManagement.optional', 'optional')})</span>
                                        </label>
                                        <textarea
                                            value={statusDescription}
                                            onChange={(e) => setStatusDescription(e.target.value)}
                                            placeholder={t('adminOrderManagement.statusChangeNotePlaceholder', 'Add a note about this status change...')}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            rows="3"
                                            data-testid="admin-order-status-modal-description"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={cancelStatusChange}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                    data-testid="admin-order-status-modal-cancel"
                                >
                                    {t('common.cancel', 'Cancel')}
                                </button>
                                <button
                                    onClick={confirmStatusChange}
                                    disabled={updatingStatus[pendingStatusChange?.orderId]}
                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-md transition-colors flex items-center gap-2"
                                    data-testid="admin-order-status-modal-confirm"
                                >
                                    {updatingStatus[pendingStatusChange?.orderId] ? (
                                        <>
                                            <RingLoader color="#ffffff" size={16} />
                                            {t('adminOrderManagement.updating', 'Updating...')}
                                        </>
                                    ) : (
                                        t('adminOrderManagement.confirmChange', 'Confirm Change')
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {orderToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-red-100 p-2">
                                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {t('adminOrderManagement.confirmDeleteOrder', 'Delete order')} #{orderToDelete.id}?
                                </h3>
                                <p className="mt-2 text-sm text-gray-600">
                                    {t('adminOrderManagement.confirmDeleteOrderMessage', 'The order and its status history will be permanently deleted. Its QR code will be unbound and returned to AVAILABLE inventory.')}
                                </p>
                                {orderToDelete.qrCode && (
                                    <p className="mt-3 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">
                                        QR: <span className="font-mono font-semibold">{orderToDelete.qrCode}</span>
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => setOrderToDelete(null)}
                                disabled={deletingOrder}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={confirmOrderDelete}
                                disabled={deletingOrder}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 flex items-center gap-2"
                                data-testid="admin-order-delete-confirm"
                            >
                                {deletingOrder ? <RingLoader color="#ffffff" size={16} /> : <TrashIcon className="h-4 w-4" />}
                                {deletingOrder ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminOrderManagement;
